import { useCallback, useEffect, useState } from "react";
import { Package, Truck, Sparkle, MapPin, Warning, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const oops = (e) => toast.error(formatApiErrorDetail(e?.response?.data?.detail));

const Row = ({ label, value, id }) => (
  <div className="flex items-baseline justify-between gap-3" data-testid={`storage-row-${id}`}>
    <dt className="text-slate-600">{label}</dt>
    <dd className="font-semibold text-[#0F172A]">{value}</dd>
  </div>
);

export default function Storage() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("space");
  const [place, setPlace] = useState("");
  const [found, setFound] = useState(null);
  const [busy, setBusy] = useState(false);
  const [want, setWant] = useState({ size_key: "10x10", months: 1, climate: false, space_id: "" });
  const [van, setVan] = useState({ vehicle_key: "cargo-van", days: 1, miles: 250 });
  const [priced, setPriced] = useState(null);
  const [pay, setPay] = useState("online");
  const [overflow, setOverflow] = useState([]);
  const [rates, setRates] = useState(null);
  const [mine, setMine] = useState({ name: "", kind: "spare back room", size_key: "10x10", where: "", miles: 3, climate: false, brand: "" });

  const load = useCallback(() => {
    api.get("/storage").then(({ data: d }) => { setData(d); setPlace((p) => p || d.my_place || ""); }).catch(oops);
    api.get("/storage/overflow").then(({ data: d }) => setOverflow(d.overflow || [])).catch(() => setOverflow([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const quote = useCallback(async () => {
    try {
      const body = tab === "van"
        ? { kind: "van", ...van, miles: Number(van.miles) || 0, days: Number(van.days) || 1 }
        : { kind: "space", size_key: want.size_key, months: Number(want.months) || 1, climate: want.climate };
      const { data: d } = await api.post("/storage/quote", body);
      setPriced(d);
    } catch (e) { oops(e); }
  }, [tab, van, want.size_key, want.months, want.climate]);

  useEffect(() => { if (data) quote(); }, [data, quote]);

  const book = async () => {
    setBusy(true);
    try {
      const body = tab === "van"
        ? { kind: "van", ...van, miles: Number(van.miles) || 0, days: Number(van.days) || 1, pay }
        : { kind: "space", size_key: want.size_key, months: Number(want.months) || 1, climate: want.climate, space_id: want.space_id, pay };
      const { data: d } = await api.post("/storage/bookings", body);
      toast.success(`${d.reference} booked`);
      if (pay === "online") {
        const { data: p } = await api.post("/payments/storage/checkout",
          { booking_id: d.id, origin_url: window.location.origin });
        if (p.url) window.location.href = p.url;
      }
      load();
    } catch (e) { oops(e); } finally { setBusy(false); }
  };

  if (!data) return <p className="py-10 text-center text-sm text-slate-500">Loading the space board…</p>;

  const sym = priced?.symbol || data.zone?.symbol || "$";
  const fmt = (n) => `${sym}${Number(n || 0).toFixed(2)}`;
  const ch = priced?.charges;

  return (
    <div className="space-y-6" data-testid="storage-page">
      <div>
        <p className="overline text-[#B3241B]">Space &amp; vans</p>
        <h1 className="font-display mt-1 flex items-center gap-2 text-3xl font-bold tracking-tight text-[#0F172A] sm:text-4xl">
          <Package size={30} weight="duotone" className="text-[#002FA7]" /> Extra space, and a van to move it
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-slate-600" data-testid="storage-explainer">
          A self-storage unit, a spare back room, a barn or a shipping container near you — and a
          U-Haul style cargo van or box truck for the run. Priced in your own money, rent it for
          yourself or for the hub when a run lands more than it can hold.
        </p>
      </div>

      <div className="border border-border bg-white p-4" data-testid="storage-find-box">
        <p className="overline text-slate-500">
          <MapPin size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Space near
        </p>
        <div className="mt-2 grid gap-2 sm:grid-cols-[2fr_auto]">
          <Input data-testid="storage-place" placeholder="Town, postcode or country"
            value={place} onChange={(e) => setPlace(e.target.value)} />
          <Button size="sm" data-testid="storage-find" disabled={busy || place.trim().length < 2}
            onClick={async () => {
              setBusy(true);
              try {
                const { data: d } = await api.post("/storage/find", { place, save: !!data.can_manage });
                setFound(d); toast.success(`${d.spaces.length} places to put it`); load();
              } catch (e) { oops(e); } finally { setBusy(false); }
            }}>
            <Sparkle size={14} className="mr-1.5" /> Find space
          </Button>
        </div>
        {found && (
          <div className="mt-3 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-3 py-2" data-testid="storage-found">
            <p className="text-sm text-[#0F172A]">{found.line}</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700" data-testid="storage-found-list">
              {found.spaces.map((s, i) => (
                <li key={i} data-testid={`storage-found-${i}`}>
                  <span className="font-semibold text-[#0F172A]">{s.name}</span>
                  {s.brand ? ` · ${s.brand}` : ""} · {s.size} · {s.miles} miles ·
                  {" "}{s.symbol}{Number(s.per_month || 0).toFixed(2)}/month
                  {s.climate ? " · climate controlled" : ""}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="border border-border bg-white p-4" data-testid="storage-book-box">
        <div className="flex gap-2">
          {["space", "van"].map((t) => (
            <button key={t} type="button" data-testid={`storage-tab-${t}`} onClick={() => setTab(t)}
              className={`tap-safe border px-4 py-2 text-xs font-semibold uppercase tracking-wider ${
                tab === t ? "border-[#0F172A] bg-[#0F172A] text-white"
                  : "border-border bg-white text-slate-700 hover:border-[#0F172A]"}`}>
              {t === "space" ? "Rent space" : "Hire a van"}
            </button>
          ))}
        </div>

        {tab === "space" ? (
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <select data-testid="storage-size" value={want.size_key} aria-label="Size"
              onChange={(e) => setWant({ ...want, size_key: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {data.sizes.map((s) => <option key={s.key} value={s.key}>{s.name} · {s.holds}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Months
              <Input data-testid="storage-months" type="number" min={1} max={24} value={want.months}
                onChange={(e) => setWant({ ...want, months: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              <input type="checkbox" data-testid="storage-climate" checked={want.climate}
                onChange={(e) => setWant({ ...want, climate: e.target.checked })} />
              Climate controlled
            </label>
            <select data-testid="storage-space-pick" value={want.space_id} aria-label="Which space"
              onChange={(e) => setWant({ ...want, space_id: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm sm:col-span-3">
              <option value="">Any space on the board</option>
              {data.spaces.map((s) => (
                <option key={s.id} value={s.id}>{s.name} · {s.size} · {s.where}</option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mt-3 grid gap-2 sm:grid-cols-3">
            <select data-testid="storage-vehicle" value={van.vehicle_key} aria-label="Vehicle"
              onChange={(e) => setVan({ ...van, vehicle_key: e.target.value })}
              className="tap-safe border border-border bg-white px-3 py-2 text-sm">
              {data.vehicles.map((v) => <option key={v.key} value={v.key}>{v.name} · {v.holds}</option>)}
            </select>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Days
              <Input data-testid="storage-days" type="number" min={1} max={60} value={van.days}
                onChange={(e) => setVan({ ...van, days: e.target.value })} className="w-20" />
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-600">
              Miles
              <Input data-testid="storage-miles" type="number" min={0} value={van.miles}
                onChange={(e) => setVan({ ...van, miles: e.target.value })} className="w-24" />
            </label>
          </div>
        )}

        {ch && (
          <div className="mt-4 border border-border bg-[#FAFAF8] p-3" data-testid="storage-quote">
            <p className="overline text-slate-500">
              {priced.kind === "van" ? priced.vehicle : priced.size} · {priced.zone_name}
            </p>
            <dl className="mt-2 space-y-1 text-sm">
              {priced.kind === "van" ? (
                <>
                  <Row label="Day rate" value={fmt(ch.day_rate)} id="dayrate" />
                  <Row label={`Hire · ${priced.days} day(s)`} value={fmt(ch.hire)} id="hire" />
                  <Row label={`Mileage · ${priced.miles} miles`} value={fmt(ch.mileage)} id="mileage" />
                </>
              ) : (
                <>
                  <Row label="Rent per month" value={fmt(ch.rent_per_month)} id="permonth" />
                  <Row label={`Rent · ${priced.months} month(s)`} value={fmt(ch.rent)} id="rent" />
                  <Row label="Deposit (refundable)" value={fmt(ch.deposit)} id="deposit" />
                </>
              )}
              <Row label="Admin fee" value={fmt(ch.admin_fee)} id="admin" />
              {ch.tax > 0 && <Row label="Tax" value={fmt(ch.tax)} id="tax" />}
            </dl>
            <p className="mt-2 border-t border-border pt-2 text-base font-bold text-[#0F172A]" data-testid="storage-total">
              Total {fmt(priced.total)} {(priced.currency || "").toUpperCase()}
            </p>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="storage-pay" value={pay} onChange={(e) => setPay(e.target.value)}
            aria-label="How you want to pay"
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            <option value="online">Pay online now</option>
            <option value="at_hub">Pay at the hub</option>
          </select>
          <Button size="sm" data-testid="storage-book" onClick={book} disabled={busy || !priced}>
            <Key size={14} className="mr-1.5" /> {tab === "van" ? "Book the van" : "Take the space"}
          </Button>
        </div>
      </div>

      <div data-testid="storage-spaces">
        <p className="overline text-slate-500">On the board</p>
        {data.spaces.length === 0 ? (
          <p className="mt-2 text-sm text-slate-500">Nothing listed yet — find space above, or list yours.</p>
        ) : (
          <div className="mt-2 grid gap-2 sm:grid-cols-3">
            {data.spaces.map((s) => (
              <div key={s.id} className="border border-border bg-white p-3" data-testid={`storage-space-${s.id}`}>
                <p className="text-sm font-semibold text-[#0F172A]">{s.name}</p>
                <p className="text-xs text-slate-500">
                  {s.brand ? `${s.brand} · ` : ""}{s.size} · {s.kind}
                  {s.where ? ` · ${s.where}` : ""}
                </p>
                <p className="mt-1 text-sm font-semibold text-[#0B5C2E]">
                  {s.symbol}{Number(s.per_month || 0).toFixed(2)}/month{s.climate ? " · climate" : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div data-testid="storage-bookings">
        <p className="overline text-slate-500">Your bookings</p>
        <ul className="mt-2 space-y-2">
          {data.bookings.map((b) => (
            <li key={b.id} className="border border-border bg-white p-3" data-testid={`storage-booking-${b.id}`}>
              <p className="text-sm font-semibold text-[#0F172A]">
                {b.reference} · {b.kind === "van" ? b.vehicle : b.size} · {b.symbol}{Number(b.total).toFixed(2)} {(b.currency || "").toUpperCase()}
              </p>
              <p className="text-xs text-slate-600">
                {b.status} · {(b.payment_status || "").replace(/_/g, " ")} ·
                {b.kind === "van" ? ` ${b.days} day(s), ${b.miles} miles` : ` ${b.months} month(s) from ${b.starts_on}`}
              </p>
              {b.status !== "released" && (
                <Button className="mt-2" size="sm" variant="outline" data-testid={`storage-release-${b.id}`}
                  onClick={async () => {
                    try { await api.post(`/storage/bookings/${b.id}/release`); toast.success("Released"); load(); }
                    catch (e) { oops(e); }
                  }}>
                  Release it
                </Button>
              )}
            </li>
          ))}
          {data.bookings.length === 0 && <li className="text-sm text-slate-500">No bookings yet.</li>}
        </ul>
      </div>

      <div className="border border-border bg-white p-4" data-testid="storage-list-yours">
        <p className="overline text-slate-500">Got space going spare? Put it on the board</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <Input data-testid="storage-mine-name" placeholder="Name it — 'Barn off Route 41'"
            value={mine.name} onChange={(e) => setMine({ ...mine, name: e.target.value })} />
          <select data-testid="storage-mine-kind" value={mine.kind} aria-label="What kind of space"
            onChange={(e) => setMine({ ...mine, kind: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.kinds.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <select data-testid="storage-mine-size" value={mine.size_key} aria-label="How big"
            onChange={(e) => setMine({ ...mine, size_key: e.target.value })}
            className="tap-safe border border-border bg-white px-3 py-2 text-sm">
            {data.sizes.map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
          </select>
          <Input data-testid="storage-mine-where" placeholder="Where is it?"
            value={mine.where} onChange={(e) => setMine({ ...mine, where: e.target.value })} />
        </div>
        <Button className="mt-2" size="sm" data-testid="storage-mine-save"
          disabled={busy || mine.name.trim().length < 2}
          onClick={async () => {
            setBusy(true);
            try {
              const { data: d } = await api.post("/storage/spaces", { ...mine, miles: Number(mine.miles) || 0 });
              toast.success(d.approved ? "It is on the board" : "Sent for approval");
              setMine({ ...mine, name: "", where: "" }); load();
            } catch (e) { oops(e); } finally { setBusy(false); }
          }}>
          List my space
        </Button>
        {(data.mine || []).length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-slate-700" data-testid="storage-mine-list">
            {data.mine.map((s) => (
              <li key={s.id}>{s.name} · {s.size} · {s.approved ? "on the board" : "waiting for approval"}</li>
            ))}
          </ul>
        )}
      </div>

      {data.can_manage && (
        <div className="border border-border bg-white p-4" data-testid="storage-admin">
          <p className="overline text-slate-500">
            <Warning size={13} className="mr-1 inline align-[-2px] text-[#B3241B]" /> Hub overflow
          </p>
          {overflow.length === 0 ? (
            <p className="mt-2 text-sm text-slate-500">Every hub is inside its capacity.</p>
          ) : (
            <ul className="mt-2 space-y-2 text-sm text-slate-700" data-testid="storage-overflow">
              {overflow.map((o) => (
                <li key={o.id} className="flex flex-wrap items-center justify-between gap-2"
                  data-testid={`storage-overflow-${o.id}`}>
                  <span>
                    {o.hub_name} needs {o.over_sqft} sq ft more than it holds — rent a {o.suggest_size}
                    {o.status === "covered" ? " · covered" : ""}
                  </span>
                  {o.status === "open" && (
                    <Button size="sm" variant="outline" data-testid={`storage-overflow-covered-${o.id}`}
                      onClick={async () => {
                        try { await api.post(`/storage/overflow/${o.id}/covered`); toast.success("Marked covered"); load(); }
                        catch (e) { oops(e); }
                      }}>
                      It is covered
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-5 border-t border-border pt-4" data-testid="storage-rates">
            <p className="overline text-slate-500">Prices for {data.zone?.country}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="storage-rates-suggest" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const { data: s } = await api.post("/storage/rates/suggest", { country: data.zone?.country || place });
                    setRates({ ...s.rates, _line: s.line, _key: s.zone_key || data.zone?.key });
                    toast.success("AI has priced it — check and save");
                  } catch (e) { oops(e); } finally { setBusy(false); }
                }}>
                <Sparkle size={14} className="mr-1.5" /> Suggest the prices
              </Button>
              <Button size="sm" variant="outline" data-testid="storage-rates-edit"
                onClick={() => setRates(rates ? null : Object.fromEntries(
                  data.rate_fields.map((f) => [f, data.rates?.[f] ?? 0])))}>
                {rates ? "Close" : "Set them by hand"}
              </Button>
            </div>
            {rates && (
              <div className="mt-3">
                {rates._line && <p className="text-sm text-slate-700">{rates._line}</p>}
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {Object.keys(rates).filter((k) => !k.startsWith("_")).map((k) => (
                    <label key={k} className="text-xs text-slate-600">
                      {k.replace(/_/g, " ")}
                      <Input data-testid={`storage-rate-${k}`} type="number" value={rates[k]}
                        onChange={(e) => setRates({ ...rates, [k]: e.target.value })} />
                    </label>
                  ))}
                </div>
                <Button className="mt-2" size="sm" data-testid="storage-rates-save" disabled={busy}
                  onClick={async () => {
                    setBusy(true);
                    try {
                      await api.post("/storage/rates", {
                        zone_key: rates._key || data.zone?.key,
                        rates: Object.fromEntries(Object.entries(rates).filter(([k]) => !k.startsWith("_"))),
                      });
                      toast.success("Prices saved"); setRates(null); load();
                    } catch (e) { oops(e); } finally { setBusy(false); }
                  }}>
                  Save the prices
                </Button>
              </div>
            )}
          </div>

          <p className="mt-4 text-xs text-slate-500" data-testid="storage-brands">
            <Truck size={13} className="mr-1 inline align-[-2px]" />
            Named options we point at: {data.brands.join(", ")}. We list and price them — booking
            with those firms is done on their own counter.
          </p>
        </div>
      )}
    </div>
  );
}
